// src/pages/DetalheCaso/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import './DetalheCaso.css';

const DetalheCaso = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caso, setCaso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gerando, setGerando] = useState(null);
  const [analiseExpandida, setAnaliseExpandida] = useState(false);
  const [acaoSelecionada, setAcaoSelecionada] = useState('');

  // Estados para controlar seções colapsáveis
  const [casoUsoExpandido, setCasoUsoExpandido] = useState(false);
  const [estrategiaExpandida, setEstrategiaExpandida] = useState(false);
  const [fundamentacaoExpandida, setFundamentacaoExpandida] = useState(false);

  // ESTADOS PARA ALTERAÇÃO DO CASO
  const [showAlterarCasoDialog, setShowAlterarCasoDialog] = useState(false);
  const [alteracoesUsuario, setAlteracoesUsuario] = useState('');
  const [alterandoCaso, setAlterandoCaso] = useState(false);

  // Configuração dos dados do advogado
  const [config, setConfig] = useState({
    advogado: '',
    oa: '',
    domicilio: 'Av. da República, 32 -1° Dto, 1050-193 Lisboa'
  });

  // Session 3: Gerar Novo Documento desde Caso
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [instrucoesEspeciais, setInstrucoesEspeciais] = useState('');
  const [gerandoNovoDoc, setGerandoNovoDoc] = useState(false);
  const [novoDocConteudo, setNovoDocConteudo] = useState('');
  const [baixandoNovoDoc, setBaixandoNovoDoc] = useState(false);

  // Session 4: Lista de documentos do caso
  const [documentosCaso, setDocumentosCaso] = useState([]);
  const [carregandoDocsCaso, setCarregandoDocsCaso] = useState(false);

  useEffect(() => {
    if (user) {
      setConfig(prev => ({
        ...prev,
        advogado: user.nome || '',
        oa: user.documento || ''
      }));
    }
  }, [user]);

  const carregarCaso = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/casos/${id}`);

      if (response.data.success) {
        const casoData = response.data.data;

        if (casoData.analise_caso && typeof casoData.analise_caso === 'object') {
          casoData.analise_caso = JSON.stringify(casoData.analise_caso);
        }

        if (casoData.documentos_sugeridos && typeof casoData.documentos_sugeridos === 'object') {
          casoData.documentos_sugeridos = JSON.stringify(casoData.documentos_sugeridos);
        }

        if (casoData.dados_cliente && typeof casoData.dados_cliente === 'object') {
          casoData.dados_cliente = JSON.stringify(casoData.dados_cliente);
        }

        setCaso(casoData);
      } else {
        setError('Erro ao carregar caso');
      }
    } catch (error) {
      console.error('Erro ao buscar caso:', error);
      setError('Erro ao carregar detalhes do caso');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregarCaso();
  }, [carregarCaso]);

  const carregarDocumentosCaso = useCallback(async () => {
    if (!caso?.id) return;
    setCarregandoDocsCaso(true);
    try {
      const response = await axios.get(`/api/documentos/caso/${caso.id}`);
      if (response.data.success) setDocumentosCaso(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar documentos do caso:', error);
    } finally {
      setCarregandoDocsCaso(false);
    }
  }, [caso?.id]);

  useEffect(() => {
    if (caso?.id) carregarDocumentosCaso();
  }, [carregarDocumentosCaso, caso?.id]);

  const formatarCodigoCaso = () => {
    if (!caso) return '';
    const data = new Date(caso.created_at);
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    const letra = String.fromCharCode(65 + (caso.id % 26));
    return `#${ano}-${mes}-${dia}${letra}`;
  };

  const formatarData = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleDateString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    }) + ' ' + d.toLocaleTimeString('pt-PT', {
      hour: '2-digit', minute: '2-digit'
    });
  };

  const handleOpenAlterarCasoDialog = () => {
    setAlteracoesUsuario('');
    setShowAlterarCasoDialog(true);
  };

  const handleCloseAlterarCasoDialog = () => {
    setShowAlterarCasoDialog(false);
    setAlteracoesUsuario('');
    setAlterandoCaso(false);
  };

  const handleAlterarCaso = async () => {
    if (!alteracoesUsuario.trim() || !caso) {
      alert('Por favor, descreva as alterações que deseja implementar.');
      return;
    }

    setAlterandoCaso(true);

    try {
      console.log('Iniciando alteração do caso via LLM...');

      const casoCompleto = {
        id: caso.id,
        name: caso.name,
        cod_cli: caso.cod_cli,
        description: caso.description,
        analise_caso: caso.analise_caso ? JSON.parse(caso.analise_caso) : null,
        documentos_sugeridos: caso.documentos_sugeridos ? JSON.parse(caso.documentos_sugeridos) : null,
        dados_cliente: caso.dados_cliente ? JSON.parse(caso.dados_cliente) : null,
        created_at: caso.created_at
      };

      const response = await axios.put(
        `/api/casos/${caso.id}/alterar-via-llm`,
        {
          alteracoes: alteracoesUsuario.trim(),
          caso_original: casoCompleto
        },
        { timeout: 300000 }
      );

      if (response.data.success) {
        alert('Caso alterado com sucesso!');
        handleCloseAlterarCasoDialog();
        await carregarCaso();
        setAnaliseExpandida(false);
        setCasoUsoExpandido(true);
        setEstrategiaExpandida(true);
        setFundamentacaoExpandida(true);
      } else {
        throw new Error(response.data.message || 'Erro ao alterar caso');
      }

    } catch (error) {
      console.error('Erro ao alterar caso:', error);

      let errorMessage = 'Erro ao processar alteração do caso';
      if (error.response) {
        errorMessage = error.response.data?.message || `Erro ${error.response.status}`;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Timeout na alteração - tente novamente';
      }

      alert(errorMessage);
    } finally {
      setAlterandoCaso(false);
    }
  };

  const handleGerarNovoDocumento = async () => {
    if (!tipoDocumento.trim() || !caso) return;
    setGerandoNovoDoc(true);
    setNovoDocConteudo('');

    try {
      const instrucoesCombinadas = [
        instrucoesEspeciais,
        '**ANÁLISE DO CASO (CONTEXTO):**',
        caso.analise_caso || ''
      ].filter(Boolean).join('\n\n').trim();

      const response = await axios.post('/api/documentos/gerar-generico', {
        nomeDocumento: tipoDocumento.trim(),
        instrucoes: instrucoesCombinadas,
        advogado: config.advogado || 'Advogado',
        oa: config.oa || 'N/A',
        domicilio: config.domicilio,
        cod_cliente: caso.cod_cli,
        id_law_case: caso.id
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao iniciar geração');
      }

      const jobId = response.data.job_id;

      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const poll = await axios.get(`/api/documentos/job/${jobId}`);
        const { estado, resultado } = poll.data;

        if (estado === 'done') {
          setNovoDocConteudo(resultado);
          await carregarDocumentosCaso();
          return;
        }
        if (estado === 'error') {
          throw new Error('Falha na geração do documento pelo servidor');
        }
      }
      throw new Error('Timeout: documento não gerado em 5 minutos');

    } catch (error) {
      console.error('Erro ao gerar novo documento:', error);
      let msg = 'Erro ao gerar documento';
      if (error.response) {
        msg = error.response.data?.message || `Erro ${error.response.status}`;
      } else if (error.code === 'ECONNABORTED') {
        msg = 'Timeout na geração - tente novamente';
      } else if (error.message) {
        msg = error.message;
      }
      alert(msg);
    } finally {
      setGerandoNovoDoc(false);
    }
  };

  const handleDownloadNovoDoc = async () => {
    if (!novoDocConteudo || !tipoDocumento) return;
    setBaixandoNovoDoc(true);
    try {
      const nomeBase = tipoDocumento.trim().replace(/\s+/g, '_');
      const response = await axios.post(
        'https://api-sniperspt.pythonanywhere.com/api/document/generate_from_text',
        {
          texto_conteudo: novoDocConteudo,
          modelo_path: 'modelo_geral.docx',
          nome_arquivo_base: nomeBase,
          return_as: 'bytes'
        },
        {
          headers: { 'X-API-KEY': 'APIKEY##', 'Content-Type': 'application/json' },
          responseType: 'blob',
          timeout: 60000
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${nomeBase}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar DOCX:', error);
      alert('Erro ao gerar arquivo DOCX');
    } finally {
      setBaixandoNovoDoc(false);
    }
  };

  const handleDownloadDocFromList = async (doc) => {
    try {
      const textoResp = await axios.get(`/api/documentos/${doc.id}/texto`);
      if (!textoResp.data.success) throw new Error('Erro ao obter texto do documento');

      const textoIntegral = textoResp.data.data.texto_integral;
      const nomeBase = doc.file_name.replace(/\.txt$/, '');

      const docxResp = await axios.post(
        'https://api-sniperspt.pythonanywhere.com/api/document/generate_from_text',
        {
          texto_conteudo: textoIntegral,
          modelo_path: 'modelo_geral.docx',
          nome_arquivo_base: nomeBase,
          return_as: 'bytes'
        },
        {
          headers: { 'X-API-KEY': 'APIKEY##', 'Content-Type': 'application/json' },
          responseType: 'blob',
          timeout: 60000
        }
      );

      const blob = new Blob([docxResp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${nomeBase}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar documento da lista:', error);
      alert('Erro ao baixar documento');
    }
  };

  const handleGerarDocx = async (tipo, titulo) => {
    setGerando(titulo);
    try {
      let dadosCliente = null;
      let analise = null;

      try {
        dadosCliente = caso.dados_cliente ? JSON.parse(caso.dados_cliente) : null;
      } catch {
        dadosCliente = null;
      }

      try {
        analise = caso.analise_caso ? JSON.parse(caso.analise_caso) : null;
      } catch {
        analise = caso.analise_caso;
      }

      const response = await axios.post('/api/documentos/gerar', {
        tipo: tipo,
        titulo: titulo,
        caso_id: caso.id,
        dados_cliente: dadosCliente,
        analise: analise
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${titulo.replace(/\s+/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao gerar documento:', error);
      alert('Erro ao gerar documento');
    } finally {
      setGerando(null);
    }
  };

  const handleVoltar = () => {
    navigate('/gestao-casos');
  };

  const handleExecutarAcao = () => {
    if (!acaoSelecionada || !temAnalise) return;

    switch (acaoSelecionada) {
      case 'gerar-docx':
        handleGerarDocx('analise', 'Análise Estratégica');
        break;
      case 'ver-json':
        setAnaliseExpandida(!analiseExpandida);
        break;
      case 'conversar-ia':
        handleConversarComIA();
        break;
      case 'alterar-caso':
        handleOpenAlterarCasoDialog();
        break;
      default:
        break;
    }

    setAcaoSelecionada('');
  };

  const handleConversarComIA = () => {
    if (!caso) return;

    let analiseCompleta = '';
    let dadosCliente = 'DADOS DE CLIENTE NAO INFORMADOS';

    try {
      const analiseObj = caso.analise_caso ? JSON.parse(caso.analise_caso) : null;
      if (analiseObj) {
        analiseCompleta = JSON.stringify(analiseObj, null, 2);
      }
    } catch {
      analiseCompleta = caso.analise_caso || '';
    }

    try {
      const clienteObj = caso.dados_cliente ? JSON.parse(caso.dados_cliente) : null;
      if (clienteObj && Object.keys(clienteObj).length > 0) {
        dadosCliente = JSON.stringify(clienteObj, null, 2);
      }
    } catch {
      if (caso.dados_cliente) {
        dadosCliente = caso.dados_cliente;
      }
    }

    const contextoCompleto = `
===== ANÁLISE DO CASO =====
${analiseCompleta}

===== DADOS DO CLIENTE =====
${dadosCliente}
    `.trim();

    navigate('/chat-with-ai', {
      state: {
        content: contextoCompleto,
        fromCaso: true,
        casoId: caso.id,
        casoNome: caso.name
      }
    });
  };

  const processarAnalise = () => {
    if (!caso?.analise_caso) {
      return { casoUso: '', estrategia: '', fundamentacao: '', temAnalise: false, analiseCompleta: '' };
    }

    try {
      const analiseObj = JSON.parse(caso.analise_caso);
      const analiseCompleta = JSON.stringify(analiseObj, null, 2);

      let casoUso = '';
      let estrategia = '';
      let fundamentacao = '';

      const baseAnalise = analiseObj.results?.analise_juridica || analiseObj.results || analiseObj;

      const casoUsoData = baseAnalise.caso_de_uso || baseAnalise.caso_de_uso || null;
      if (casoUsoData) {
        casoUso += '📋 CASO DE USO\n\n';
        if (casoUsoData.contexto) {
          casoUso += `Contexto:\n${casoUsoData.contexto}\n\n`;
        }
        if (casoUsoData.area_direito || casoUsoData.area_do_direito) {
          casoUso += `Área do Direito: ${casoUsoData.area_direito || casoUsoData.area_do_direito}\n\n`;
        }
        if (casoUsoData.especificidade) {
          casoUso += `Especificidade:\n${casoUsoData.especificidade}\n\n`;
        }
      }

      const queryInicial = baseAnalise.query_inicial || analiseObj.query_inicial || null;
      if (queryInicial) {
        casoUso += `📝 QUERY INICIAL\n${queryInicial}\n\n`;
      }

      const estrategiaData = baseAnalise.estrategia_geral || null;
      if (estrategiaData) {
        estrategia += '⚖️ ESTRATÉGIA GERAL\n\n';

        if (estrategiaData.via_principal) {
          estrategia += `Via Principal:\n${estrategiaData.via_principal}\n\n`;
        } else if (estrategiaData.plano_acao) {
          estrategia += `Plano de Ação:\n${estrategiaData.plano_acao}\n\n`;
        }

        if (estrategiaData.desdobramentos) {
          estrategia += `Desdobramentos:\n${estrategiaData.desdobramentos}\n\n`;
        }

        if (Array.isArray(estrategiaData.vias_principais)) {
          estrategia += 'VIAS PRINCIPAIS:\n';
          estrategiaData.vias_principais.forEach((via, idx) => {
            estrategia += `\n${idx + 1}. ${via.via || ''}\n`;
            estrategia += `${via.descricao || ''}\n`;
          });
          estrategia += '\n';
        }

        if (estrategiaData.riscos_e_oportunidades) {
          estrategia += 'RISCOS E OPORTUNIDADES:\n';
          if (estrategiaData.riscos_e_oportunidades.riscos) {
            estrategia += `\n✗ Riscos:\n${estrategiaData.riscos_e_oportunidades.riscos}\n`;
          }
          if (estrategiaData.riscos_e_oportunidades.oportunidades) {
            estrategia += `\n✓ Oportunidades:\n${estrategiaData.riscos_e_oportunidades.oportunidades}\n`;
          }
          estrategia += '\n';
        } else if (estrategiaData.riscos_oportunidades) {
          estrategia += 'RISCOS E OPORTUNIDADES:\n';
          if (estrategiaData.riscos_oportunidades.riscos) {
            estrategia += `\n✗ Riscos:\n${estrategiaData.riscos_oportunidades.riscos}\n`;
          }
          if (estrategiaData.riscos_oportunidades.oportunidades) {
            estrategia += `\n✓ Oportunidades:\n${estrategiaData.riscos_oportunidades.oportunidades}\n`;
          }
          estrategia += '\n';
        }

        if (estrategiaData.alternativas_estrategicas) {
          if (typeof estrategiaData.alternativas_estrategicas === 'string') {
            estrategia += `ALTERNATIVAS ESTRATÉGICAS:\n${estrategiaData.alternativas_estrategicas}\n\n`;
          } else if (Array.isArray(estrategiaData.alternativas_estrategicas)) {
            estrategia += 'ALTERNATIVAS ESTRATÉGICAS:\n';
            estrategiaData.alternativas_estrategicas.forEach((alt, idx) => {
              estrategia += `\n${idx + 1}. ${alt.alternativa || ''}\n`;
              estrategia += `${alt.descricao || ''}\n`;
            });
            estrategia += '\n';
          }
        }
      }

      const fundamentacaoData = baseAnalise.fundamentacao_legal ||
                               analiseObj.results?.fundamentacao_legal ||
                               analiseObj.fundamentacao_legal || null;

      if (fundamentacaoData) {
        fundamentacao += '📚 FUNDAMENTAÇÃO LEGAL\n\n';

        if (Array.isArray(fundamentacaoData.artigos_principais)) {
          fundamentacao += '⚖️ ARTIGOS PRINCIPAIS:\n\n';
          fundamentacaoData.artigos_principais.forEach((art, idx) => {
            fundamentacao += `${idx + 1}. ${art.artigo || ''}\n`;
            if (art.codigo) fundamentacao += `   Código: ${art.codigo}\n`;
            if (art.titulo) fundamentacao += `   Título: ${art.titulo}\n`;
            if (art.relevancia) fundamentacao += `   Relevância: ${art.relevancia}\n`;
            fundamentacao += '\n';
          });
        }

        if (Array.isArray(fundamentacaoData.artigos_expandidos)) {
          fundamentacao += '📖 ARTIGOS EXPANDIDOS:\n\n';
          fundamentacaoData.artigos_expandidos.forEach((art, idx) => {
            fundamentacao += `${idx + 1}. ${art.artigo || ''}\n`;
            if (art.codigo) fundamentacao += `   Código: ${art.codigo}\n`;
            if (art.titulo) fundamentacao += `   Título: ${art.titulo}\n`;
            if (art.relevancia) fundamentacao += `   Relevância: ${art.relevancia}\n`;
            fundamentacao += '\n';
          });
        }

        if (fundamentacaoData.palavras_chaves_jurisprudencia) {
          fundamentacao += '🔍 PALAVRAS-CHAVE PARA JURISPRUDÊNCIA:\n';
          if (Array.isArray(fundamentacaoData.palavras_chaves_jurisprudencia)) {
            fundamentacaoData.palavras_chaves_jurisprudencia.forEach((palavra) => {
              fundamentacao += `  • ${palavra}\n`;
            });
          }
          fundamentacao += '\n';
        }

        if (Array.isArray(fundamentacaoData) && fundamentacaoData.length > 0 && fundamentacaoData[0].artigo) {
          fundamentacao = '📚 FUNDAMENTAÇÃO LEGAL\n\n';
          fundamentacaoData.forEach((art, idx) => {
            fundamentacao += `${idx + 1}. ${art.artigo || 'Artigo'}\n`;
            if (art.codigo) fundamentacao += `   Código: ${art.codigo}\n`;
            if (art.titulo) fundamentacao += `   Título: ${art.titulo}\n`;
            if (art.relevancia) fundamentacao += `   Relevância: ${art.relevancia}\n`;
            fundamentacao += '\n';
          });
        }
      }

      if (!casoUso && !estrategia && !fundamentacao) {
        casoUso = 'Estrutura de dados não reconhecida. Visualize o JSON completo para mais detalhes.';
      }

      return {
        casoUso: casoUso || 'Informações do caso de uso não disponíveis',
        estrategia: estrategia || 'Estratégia geral não disponível',
        fundamentacao: fundamentacao || 'Fundamentação legal não disponível',
        temAnalise: true,
        analiseCompleta
      };

    } catch {
      const textoOriginal = String(caso.analise_caso);
      return {
        casoUso: textoOriginal,
        estrategia: '',
        fundamentacao: '',
        temAnalise: true,
        analiseCompleta: textoOriginal
      };
    }
  };

  if (loading) {
    return (
      <div className="detalhe-caso-container">
        <div className="loading">Carregando caso...</div>
      </div>
    );
  }

  if (error || !caso) {
    return (
      <div className="detalhe-caso-container">
        <div className="error-message">{error || 'Caso não encontrado'}</div>
        <button className="btn-voltar" onClick={handleVoltar}>Voltar</button>
      </div>
    );
  }

  let nomeCliente = 'Nome não disponível';
  if (caso.dados_cliente) {
    try {
      const dadosCliente = JSON.parse(caso.dados_cliente);
      if (dadosCliente && dadosCliente.nome) {
        nomeCliente = String(dadosCliente.nome);
      }
    } catch {
      nomeCliente = 'Nome não disponível';
    }
  }

  const { casoUso, estrategia, fundamentacao, temAnalise, analiseCompleta } = processarAnalise();

  return (
    <div className="detalhe-caso-container">
      <div className="detalhe-caso-header">
        <button className="btn-voltar" onClick={handleVoltar}>
          ← Voltar
        </button>
        <div className="caso-info">
          <h2>{`📂 CASO ${formatarCodigoCaso()} - ${String(caso.name || '')}`}</h2>
          {caso.cod_cli && (
            <p className="cliente-info">
              {`Cliente: ${String(caso.cod_cli)} - ${nomeCliente}`}
            </p>
          )}
          {user && (user.nome || user.documento) && (
            <p className="advogado-info">
              Advogado: <strong>{config.advogado || 'Não informado'}</strong>
              {config.oa && ` | OA: ${config.oa}`}
            </p>
          )}
        </div>
      </div>

      <div className="detalhe-caso-content">

        {/* Session 1: Descrição Original do Caso */}
        {caso.description && caso.description.trim() && (
          <div className="secao-descricao">
            <div className="secao-header">
              <h3>📝 DESCRIÇÃO ORIGINAL DO CASO</h3>
            </div>
            <div className="descricao-content">
              <p className="descricao-texto">{caso.description}</p>
            </div>
          </div>
        )}

        {/* Session 2: Análise Estratégica (unchanged) */}
        <div className="secao-analise">
          <div className="secao-header">
            <h3>🧠 ANÁLISE ESTRATÉGICA</h3>
            <div className="secao-header-actions">
              <div className="acao-select-container">
                <label htmlFor="acao-analise">Escolher ação:</label>
                <select
                  id="acao-analise"
                  className="acao-select"
                  value={acaoSelecionada}
                  onChange={(e) => setAcaoSelecionada(e.target.value)}
                  disabled={!temAnalise || gerando}
                >
                  <option value="">Selecione...</option>
                  <option value="ver-json">Ver JSON da Análise</option>
                  <option value="conversar-ia">Conversar com a IA</option>
                  <option value="alterar-caso">Alterar o Caso</option>
                </select>
                <button
                  className="btn-executar-acao"
                  onClick={handleExecutarAcao}
                  disabled={!acaoSelecionada || !temAnalise || gerando}
                >
                  {gerando ? 'Processando...' : 'Executar'}
                </button>
              </div>
            </div>
          </div>

          <div className="analise-content">
            {analiseExpandida ? (
              <div className="analise-texto">
                <pre>{analiseCompleta}</pre>
              </div>
            ) : (
              <div className="analise-secoes">
                <div className="subsecao-colapsavel">
                  <div
                    className="subsecao-header"
                    onClick={() => setCasoUsoExpandido(!casoUsoExpandido)}
                  >
                    <h4>📋 CASO DE USO</h4>
                    <span className={`seta ${casoUsoExpandido ? 'expandida' : ''}`}>▼</span>
                  </div>
                  {casoUsoExpandido && (
                    <div className="subsecao-content">
                      <pre>{casoUso}</pre>
                    </div>
                  )}
                </div>

                <div className="subsecao-colapsavel">
                  <div
                    className="subsecao-header"
                    onClick={() => setEstrategiaExpandida(!estrategiaExpandida)}
                  >
                    <h4>⚖️ ESTRATÉGIA GERAL</h4>
                    <span className={`seta ${estrategiaExpandida ? 'expandida' : ''}`}>▼</span>
                  </div>
                  {estrategiaExpandida && (
                    <div className="subsecao-content">
                      <pre>{estrategia}</pre>
                    </div>
                  )}
                </div>

                <div className="subsecao-colapsavel">
                  <div
                    className="subsecao-header"
                    onClick={() => setFundamentacaoExpandida(!fundamentacaoExpandida)}
                  >
                    <h4>📚 FUNDAMENTAÇÃO LEGAL</h4>
                    <span className={`seta ${fundamentacaoExpandida ? 'expandida' : ''}`}>▼</span>
                  </div>
                  {fundamentacaoExpandida && (
                    <div className="subsecao-content">
                      <pre>{fundamentacao}</pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Session 3: Gerar Novo Documento desde Caso */}
        <div className="secao-gerar-documento">
          <div className="secao-header">
            <h3>📄 GERAR NOVO DOCUMENTO DESDE CASO</h3>
          </div>
          <div className="gerar-doc-body">
            <div className="gerar-doc-form">
              <div className="gerar-doc-field">
                <label htmlFor="tipo-documento">Tipo de Documento</label>
                <input
                  id="tipo-documento"
                  type="text"
                  className="gerar-doc-input"
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                  placeholder="Ex: Contestação, Petição Inicial, Recurso..."
                  disabled={gerandoNovoDoc}
                />
              </div>
              <div className="gerar-doc-field">
                <label htmlFor="instrucoes-especiais">Instruções Especiais (opcional)</label>
                <textarea
                  id="instrucoes-especiais"
                  className="gerar-doc-textarea"
                  value={instrucoesEspeciais}
                  onChange={(e) => setInstrucoesEspeciais(e.target.value)}
                  placeholder="Instruções adicionais para personalizar o documento..."
                  rows={4}
                  disabled={gerandoNovoDoc}
                />
              </div>
              <button
                className="btn-gerar-novo-doc"
                onClick={handleGerarNovoDocumento}
                disabled={gerandoNovoDoc || !tipoDocumento.trim()}
              >
                {gerandoNovoDoc ? (
                  <><span className="spinner-small"></span> Gerando...</>
                ) : (
                  'Gerar Documento'
                )}
              </button>
            </div>

            {novoDocConteudo && (
              <div className="gerar-doc-resultado">
                <div className="gerar-doc-resultado-header">
                  <span className="gerar-doc-resultado-label">Documento gerado:</span>
                  <button
                    className="btn-download-docx"
                    onClick={handleDownloadNovoDoc}
                    disabled={baixandoNovoDoc}
                  >
                    {baixandoNovoDoc ? (
                      <><span className="spinner-small"></span> Gerando...</>
                    ) : (
                      '📄 Download .DOCX'
                    )}
                  </button>
                </div>
                <div className="conteudo-gerado-container">
                  <div className="markdown-content">
                    <ReactMarkdown>{novoDocConteudo}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Session 4: Documentos Associados ao Caso */}
        <div className="secao-docs-caso">
          <div className="secao-header">
            <h3>🗂️ DOCUMENTOS ASSOCIADOS AO CASO</h3>
            <button
              className="btn-recarregar-docs"
              onClick={carregarDocumentosCaso}
              disabled={carregandoDocsCaso}
              title="Recarregar lista"
            >
              {carregandoDocsCaso ? '...' : '↻'}
            </button>
          </div>
          <div className="docs-caso-body">
            {carregandoDocsCaso ? (
              <p className="docs-caso-loading">Carregando documentos...</p>
            ) : documentosCaso.length === 0 ? (
              <p className="docs-caso-vazio">Nenhum documento associado a este caso.</p>
            ) : (
              <table className="docs-caso-tabela">
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Data</th>
                    <th>Prévia</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {documentosCaso.map((doc) => (
                    <tr key={doc.id}>
                      <td className="docs-caso-nome">
                        {doc.file_name.replace(/\.txt$/, '')}
                      </td>
                      <td className="docs-caso-data">
                        {formatarData(doc.created_at)}
                      </td>
                      <td className="docs-caso-preview">
                        {doc.texto_preview ? `${doc.texto_preview}...` : '-'}
                      </td>
                      <td className="docs-caso-acao">
                        <button
                          className="btn-download-doc-lista"
                          onClick={() => handleDownloadDocFromList(doc)}
                          title="Download .DOCX"
                        >
                          ⬇
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>

      {/* Dialog: Alteração do Caso */}
      {showAlterarCasoDialog && (
        <div className="dialog-overlay" onClick={handleCloseAlterarCasoDialog}>
          <div className="dialog-content dialog-content-medium" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>✏️ Alteração do Caso</h3>
              <button className="dialog-close" onClick={handleCloseAlterarCasoDialog}>×</button>
            </div>

            <div className="dialog-body">
              <p className="dialog-instrucao">
                Descreva as alterações que deseja implementar na análise do caso.
                A IA irá processar suas solicitações e gerar uma nova análise atualizada.
              </p>

              <div className="dialog-form-group">
                <label htmlFor="alteracoes">Alterações solicitadas:</label>
                <textarea
                  id="alteracoes"
                  className="dialog-textarea"
                  value={alteracoesUsuario}
                  onChange={(e) => setAlteracoesUsuario(e.target.value)}
                  placeholder="Exemplo: Incluir mais detalhes sobre a fundamentação do artigo 13º-A do NRAU, rever estratégia, alterar dados do caso..."
                  rows={8}
                  disabled={alterandoCaso}
                />
              </div>
            </div>

            <div className="dialog-footer">
              <button
                className="dialog-btn-cancelar"
                onClick={handleCloseAlterarCasoDialog}
                disabled={alterandoCaso}
              >
                Cancelar
              </button>
              <button
                className="dialog-btn-gerar"
                onClick={handleAlterarCaso}
                disabled={alterandoCaso || !alteracoesUsuario.trim()}
              >
                {alterandoCaso ? (
                  <>
                    <span className="spinner-small"></span>
                    Processando alterações...
                  </>
                ) : (
                  'Alterar Caso'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalheCaso;
